import React from 'react';
import {
  Box,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import MainLayout from '../../../components/layout/MainLayout';
import { staffApi, Staff } from '../api';

const statusColor = (status?: string) => {
  const code = String(status || '').toUpperCase();
  if (code === 'ACTIVE') return 'success';
  if (code === 'RESIGNED') return 'warning';
  if (code === 'INACTIVE') return 'default';
  return 'info';
};

const StaffListPage: React.FC = () => {
  const [staff, setStaff] = React.useState<Staff[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await staffApi.listStaff({ includeResigned: 'false' });
      setStaff(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load staff list');
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = staff.filter(
    (s) =>
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      (s.employeeCode || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.department?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Box>
            <h2 style={{ margin: 0 }}>Staff List</h2>
            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>View all active staff members and their profiles</p>
          </Box>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Paper sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, code, or department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ p: 2 }}
            variant="outlined"
          />
        </Paper>

        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredStaff.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>No staff found</Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Photo</TableCell>
                  <TableCell>Employee Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStaff.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.photo ? <img src={row.photo} alt={row.firstName} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} /> : '-'}
                    </TableCell>
                    <TableCell>{row.employeeCode}</TableCell>
                    <TableCell>{row.firstName} {row.middleName} {row.lastName}</TableCell>
                    <TableCell>{row.department?.name || '-'}</TableCell>
                    <TableCell>{row.designation?.name || '-'}</TableCell>
                    <TableCell>{row.mobile || '-'}</TableCell>
                    <TableCell><Chip size="small" label={row.status} color={statusColor(row.status) as any} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Container>
    </MainLayout>
  );
};

export default StaffListPage;
